import { useState } from 'react';
import ArrivalsFor from './battlefieldDeploy/ArrivalsFor';
import clsx from '../utils/clsx';
import common from '../styles/common.module.css';
import styles from '../styles/Battlefield.module.css';
import NumberInput from './NumberInput';
import TeamField, { useTeamNaming } from './battlefieldDeploy/TeamField';
import RingFlyout from './battlefieldDeploy/RingFlyout';

/**
 * WHERE THE FLAG STANDS, in the bar that controls it. The map draws the
 * marker; only this says which storey it is on, which is the one thing a
 * flat picture cannot tell you.
 */
function DeployWhere({ marker, air }) {
  return (
    <span className={styles.deployWhere} data-testid="deploy-where">
      ⚑ {marker.x.toFixed(1)}, {marker.z.toFixed(1)}
      {marker.y ? ` · storey ${marker.y.toFixed(1)} m` : ''}
      {air > 0 ? ` · ${air.toFixed(1)} m up` : ''}
    </span>
  );
}

/**
 * HOW HIGH THE BANNER FLIES. The wheel over the flag is the gesture; this
 * is the number, because a gesture with no readout is a guess - and ⤓ is
 * the way back to the ground, which has to be one click (v8.31.0).
 */
function AirField({ air, onAir }) {
  return (
    <>
      <label className={common.label}>
        Air
        <NumberInput
          className={clsx(common.input, styles.deployRadius)}
          aria-label="Deployment height"
          value={air}
          min={0}
          step={1}
          onChange={(v) => onAir?.(v)}
        />
      </label>
      {air > 0 && (
        <button
          type="button"
          className={common.btnSmall}
          data-testid="deploy-ground"
          title="Put the banner back on the ground (⤓)"
          onClick={() => onAir?.(0)}
        >
          ⤓
        </button>
      )}
    </>
  );
}

const SHAPES = [
  ['line', '▬', 'A line, one per grid step'],
  ['block', '▦', 'Fill the area you drag, row by row'],
];

/**
 * WHAT A DRAG DRAWS. It was alt-to-switch and nothing else, which asks the
 * operating system's permission: Windows gives Alt to the menu bar and
 * several window managers take alt-drag to move the window, so on some
 * machines the block could not be drawn (v8.30.0). The buttons are the
 * truth; Alt still works, and means "the other one, just for this drag".
 */
function ShapeGroup({ shape, onShape }) {
  return (
    <div className={styles.shapeGroup} role="group" aria-label="Formation shape">
      {SHAPES.map(([key, icon, title]) => (
        <button
          key={key}
          type="button"
          className={clsx(common.btnSmall, shape === key && styles.shapeActive)}
          data-testid={`deploy-shape-${key}`}
          aria-pressed={shape === key}
          title={title}
          onClick={() => onShape?.(key)}
        >
          {icon}
        </button>
      ))}
    </div>
  );
}

/**
 * `Ring…`, and re-deploy - SHORT ON PURPOSE: "⟳ Re-deploy everyone" is the
 * widest thing in a row that must never wrap, and the moment it does the
 * bar starts eating the map (v8.23.0). The title says the rest.
 */
function RingButtons({ ringOpen, onToggle, onRedeploy }) {
  return (
    <>
      <button
        type="button"
        className={clsx(common.btnSmall, ringOpen && styles.deployRingOpen)}
        data-testid="deploy-ring-toggle"
        aria-expanded={ringOpen}
        aria-controls="deploy-ring-flyout"
        onClick={onToggle}
      >
        Ring…
      </button>
      <button
        type="button"
        className={common.btnSmall}
        data-testid="deploy-redeploy"
        title="Re-deploy everyone around the marker"
        onClick={onRedeploy}
      >
        ⟳ All
      </button>
    </>
  );
}

/**
 * Deploy mode's controls: ONE ROW, and a flyout for the rest.
 *
 * It used to render `.buildBar` - a class written to live inside Build's
 * drawer, which Deploy does not have. Bare in the stage it became a
 * full-width column of seven labelled controls that ate the bottom of
 * the stage, crushed the map to a strip and left the room rail floating
 * over most of what remained. Half of Deploy mode could not be clicked,
 * and an E2E drag aimed at the middle of the map landed on the Spacing
 * dropdown (v8.23.0).
 *
 * THE SHAPE WAS ALREADY HERE. `.deployBar` - a compact bar centred at
 * the bottom - is what the empty state used all along; planting a marker
 * threw it away. Now both states wear it.
 *
 * WHAT STAYS IN THE ROW is what you touch while looking at the map: the
 * radius, and re-deploy. Spacing, arc and fill order are set
 * once and then left alone, so they go behind `Ring…` and open UPWARD
 * over the map - over empty air, rather than pushing the map aside.
 */
export default function BattlefieldDeployBar(props) {
  const { marker, shape = 'line', onShape, onAir, onRing, onRedeploy, capacityWarning, teams = [], onTeam } = props;
  const [ringOpen, setRingOpen] = useState(false);
  const naming = useTeamNaming();
  // A team's own arrival point (plan scene-editor-library, B): offered where it can be stored and there are teams.
  const arrivalsFor = props.onPlantFor && teams.length > 0 && (
    <ArrivalsFor
      teams={teams}
      arrivals={props.arrivals}
      plantFor={props.plantFor}
      onPlantFor={props.onPlantFor}
      onRemove={props.onRemoveArrival}
    />
  );
  // While a team's point is being placed, the main marker's controls step aside: they would not be about it.
  if (!marker || props.plantFor) {
    return (
      <div className={styles.deployBar} data-testid="deploy-bar">
        {arrivalsFor}
        {!props.plantFor && <span className={styles.readoutHint}>Click the map to plant the deployment marker.</span>}
      </div>
    );
  }
  const air = marker.air ?? 0;
  return (
    <div className={styles.deployBar} data-testid="deploy-bar">
      {arrivalsFor}
      <DeployWhere marker={marker} air={air} />
      <AirField air={air} onAir={onAir} />
      {onTeam && <TeamField marker={marker} teams={teams} onTeam={onTeam} naming={naming} />}
      <label className={common.label}>
        Radius
        <NumberInput
          className={clsx(common.input, styles.deployRadius)}
          aria-label="Ring radius"
          value={marker.ring.radius}
          min={0.5}
          step={0.5}
          onChange={(v) => onRing({ radius: v })}
        />
      </label>
      <ShapeGroup shape={shape} onShape={onShape} />
      <RingButtons ringOpen={ringOpen} onToggle={() => setRingOpen((v) => !v)} onRedeploy={onRedeploy} />
      {capacityWarning && (
        <span className={styles.deployWarning} data-testid="deploy-capacity-warning">
          {capacityWarning}
        </span>
      )}
      {ringOpen && (
        <RingFlyout
          ring={marker.ring}
          onRing={onRing}
          redeployOnMapLoad={props.redeployOnMapLoad}
          onRedeployOnMapLoad={props.onRedeployOnMapLoad}
        />
      )}
    </div>
  );
}
